/* eslint-disable react-refresh/only-export-components */
/**
 * LeadersChatContext - State management for the Leaders Chat channel
 *
 * Provides real-time message subscriptions, typing indicators, unread counts,
 * and message actions (send, delete, pin, mark read) for the encrypted
 * Leaders Chat. Only initializes subscriptions if the authenticated user
 * has 'leader' or 'admin' role.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useEncryption } from './EncryptionContext';
import { isLeaderOrAdmin, isAdmin as isAdminCheck } from '@/utils/permissions';
import {
  subscribeToLeadersChatMessages,
  subscribeToLeadersChatTyping,
  subscribeToLeadersChatUnreadCount,
  subscribeToLeadersChatMetadata,
  markLeadersChatRead,
  deleteLeadersChatMessage,
  pinLeadersChatMessage,
  setLeadersChatTypingStatus,
} from '@/services/leadersChat';
import type {
  LeadersChatMessage,
  LeadersChatMessageInput,
  LeadersChatContextValue,
  LeadersChatUserMetadata,
} from '@/types/leadersChat';

const LeadersChatContext = createContext<LeadersChatContextValue | null>(null);

interface LeadersChatProviderProps {
  children: ReactNode;
}

export function LeadersChatProvider({ children }: LeadersChatProviderProps) {
  const { authState } = useAuth();

  const [allMessages, setAllMessages] = useState<LeadersChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typingUsers, setTypingUsers] = useState<{ userId: string; userName: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState<LeadersChatUserMetadata | null>(null);

  const user = authState.status === 'authenticated' ? authState.user : null;
  const profile = authState.status === 'authenticated' ? authState.profile : null;

  const { ready: encryptionReady } = useEncryption();

  const hasAccess = !!profile && isLeaderOrAdmin(profile);
  const userIsAdmin = !!profile && isAdminCheck(profile);

  // Typing debounce ref
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ==================== Subscriptions ====================

  // Subscribe to user's leaders chat metadata (joinedAt gating)
  useEffect(() => {
    if (!user || !hasAccess) {
      setMetadata(null);
      return;
    }

    const unsub = subscribeToLeadersChatMetadata(user.uid, (meta) => {
      setMetadata(meta);
    });

    return unsub;
  }, [user, hasAccess]);

  // Subscribe to messages — re-subscribe when encryption becomes ready so
  // the snapshot handler can actually decrypt the messages (on first load,
  // the snapshot may fire before the encryption service has initialised).
  useEffect(() => {
    if (!hasAccess) {
      setAllMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    let isFirst = true;

    const unsub = subscribeToLeadersChatMessages((messages) => {
      setAllMessages(messages);
      if (isFirst) {
        setLoading(false);
        isFirst = false;
      }
    });

    return unsub;
  }, [hasAccess, encryptionReady]);

  // Subscribe to unread count
  useEffect(() => {
    if (!user || !hasAccess) {
      setUnreadCount(0);
      return;
    }

    const unsub = subscribeToLeadersChatUnreadCount(user.uid, (count) => {
      setUnreadCount(count);
    });

    return unsub;
  }, [user, hasAccess]);

  // Subscribe to typing indicators
  useEffect(() => {
    if (!user || !hasAccess) {
      setTypingUsers([]);
      return;
    }

    const unsub = subscribeToLeadersChatTyping(user.uid, (users) => {
      setTypingUsers(users);
    });

    return unsub;
  }, [user, hasAccess]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // ==================== Filtered Messages (joinedAt gating) ====================

  const messages = useMemo(() => {
    if (!metadata?.joinedAt) return allMessages.filter((msg) => !msg.isDeleted);

    const joinedAtMillis = metadata.joinedAt.toMillis();
    return allMessages.filter((msg) => {
      if (msg.isDeleted) return false; // Hide deleted messages
      if (!msg.timestamp) return true; // Pending messages without timestamp
      return msg.timestamp.toMillis() >= joinedAtMillis;
    });
  }, [allMessages, metadata]);

  // ==================== Actions ====================

  const sendMessage = useCallback(async (_input: LeadersChatMessageInput): Promise<string> => {
    // Actual sending is handled by PendingMessagesContext for optimistic updates
    // This is a no-op placeholder - the real send goes through sendOptimistically
    throw new Error('Use sendOptimistically from PendingMessagesContext for Leaders Chat messages');
  }, []);

  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!user) throw new Error('Not authenticated');
      await deleteLeadersChatMessage(messageId, user.uid, userIsAdmin);
    },
    [user, userIsAdmin]
  );

  const pinMessage = useCallback(
    async (messageId: string, isPinned: boolean) => {
      if (!user) throw new Error('Not authenticated');
      await pinLeadersChatMessage(messageId, user.uid, isPinned);
    },
    [user]
  );

  const markAsRead = useCallback(async () => {
    if (!user) return;
    await markLeadersChatRead(user.uid);
  }, [user]);

  const setTyping = useCallback(
    (isTyping: boolean) => {
      if (!user || !profile || !hasAccess) return;

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      setLeadersChatTypingStatus(user.uid, profile.displayName, isTyping).catch(console.error);

      // Auto-clear typing after 5 seconds
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(() => {
          setLeadersChatTypingStatus(user.uid, profile.displayName, false).catch(console.error);
        }, 5000);
      }
    },
    [user, profile, hasAccess]
  );

  // ==================== Context Value ====================

  const value = useMemo<LeadersChatContextValue>(
    () => ({
      messages,
      unreadCount,
      typingUsers,
      loading,
      hasAccess,
      sendMessage,
      deleteMessage,
      pinMessage,
      markAsRead,
      setTyping,
    }),
    [
      messages,
      unreadCount,
      typingUsers,
      loading,
      hasAccess,
      sendMessage,
      deleteMessage,
      pinMessage,
      markAsRead,
      setTyping,
    ]
  );

  return <LeadersChatContext.Provider value={value}>{children}</LeadersChatContext.Provider>;
}

export function useLeadersChat(): LeadersChatContextValue {
  const context = useContext(LeadersChatContext);
  if (!context) {
    throw new Error('useLeadersChat must be used within a LeadersChatProvider');
  }
  return context;
}
